<template>
  <div class="max-w-5xl mx-auto">
    <h1 class="pb-6 text-2xl font-semibold text-vice-teal-900">Your reflection</h1>

    <div class="grid grid-cols-10 gap-8">
      <div class="col-span-10">
        <my-personal-goals v-if="this.$page.props.session.reflection?.my_goals" />
        <what-card @fieldChanged="updateField" />
        <so-what-card @fieldChanged="updateField" />
        <now-what-card @fieldChanged="updateField" />

        <div class="mt-6 flex justify-end items-center">
          <action-message :on="this.is_saved" message="Your changes have been saved." />
          <button
            type="button"
            :disabled="isSaving"
            @click="downloadReflection"
            class="
              mr-3
              px-4
              py-1.5
              border border-gray-300
              rounded-md
              text-xs
              font-medium
              bg-white
              text-vice-teal-900
              hover:bg-gray-50
              hover:border-vice-teal-700
              focus:outline-none
              inline-flex
              items-center
              tracking-widest
              focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
            "
          >
            Download
          </button>
          <Button
            type="button"
            :disabled="isSaving"
            @click="saveAllReflections"
            class="
              px-4
              py-1.5
              border border-gray-300
              rounded-md
              text-xs
              font-medium
              bg-white
              hover:bg-gray-50
              focus:outline-none
            "
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Inertia } from '@inertiajs/inertia';
import MyPersonalGoals from './Reflection/MyPersonalGoals.vue';
import WhatCard from './Reflection/WhatCard.vue';
import SoWhatCard from './Reflection/SoWhatCard.vue';
import NowWhatCard from './Reflection/NowWhatCard.vue';
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import Button from '@/Jetstream/Button.vue';

const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes to your reflection. Are you sure you want to continue without saving?';

export default {
  name: 'Reflection',
  components: {
    Button,
    NowWhatCard,
    SoWhatCard,
    WhatCard,
    MyPersonalGoals,
    ActionMessage,
  },
  data() {
    return {
      downloadUrl: this.$page.props.downloadReflectionUrl,
      reflectionData: {
        what: this.$page.props.session.reflection?.what,
        so_what: this.$page.props.session.reflection?.so_what,
        now_what: this.$page.props.session.reflection?.now_what,
      },
      is_saved: false,
      isSaving: false,
      hasUnsavedChanges: false,
      removeInertiaGuard: null,
    };
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.removeInertiaGuard = Inertia.on('before', () => this.confirmDiscardChanges());
  },
  unmounted() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.removeInertiaGuard?.();
  },
  methods: {
    updateField(data) {
      this.reflectionData[data.field] = data.value;
      this.hasUnsavedChanges = true;
    },

    handleBeforeUnload(event) {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    },

    confirmDiscardChanges() {
      // eslint-disable-next-line no-alert -- native dialog matches the browser's own beforeunload prompt
      return !this.hasUnsavedChanges || window.confirm(UNSAVED_CHANGES_MESSAGE);
    },

    buildPayload() {
      return {
        what: this.reflectionData.what,
        so_what: this.reflectionData.so_what,
        now_what: this.reflectionData.now_what,
        session_id: this.$page.props.session.id,
      };
    },

    async saveReflection(payload = null) {
      const data = payload || this.buildPayload();
      this.isSaving = true;
      try {
        await axios.post(route('reflection.update'), data);
        this.is_saved = true;
        this.hasUnsavedChanges = false;
        setTimeout(() => {
          this.is_saved = false;
        }, 2000);
        return true;
      } catch (error) {
        console.error('Failed to save reflection:', error);
        return false;
      } finally {
        this.isSaving = false;
      }
    },

    async saveAllReflections() {
      await this.saveReflection();
    },

    async downloadReflection() {
      const ok = await this.saveReflection(this.buildPayload());
      if (ok) {
        window.location.href = this.downloadUrl;
      }
    },
  },
};
</script>
